import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';

import { NavigationIntentService } from 'app/shared/services/navigation-intent.service';
import { AuthService } from '../../auth/auth.service';
import { StatsigService } from 'app/shared/services/statsig.service';
import { SkeletonLoaderService } from 'app/shared/services/skeleton-loader.service';
import { VideoManagerService } from 'app/shared/services/video-manager.service';

import { SvgIconComponent } from '../../shared/components/svg-icon/svg-icon.component';
import { DiscoverCardBadgeComponent } from './discover-card-badge/discover-card-badge.component';
import { PlayVoiceButtonComponent } from '../../shared/components/play-voice-button/play-voice-button.component';
import { VideoComponent } from '../../shared/components/video/video.component';
import { ImageComponent } from '../../shared/components/image/image.component';

import { NumberCountPipe } from 'app/pipes/number-count.pipe';

import { ISvgConfig } from '../../shared/interfaces/svg.interfaces';
import { IDiscoverItem } from '../../interfaces/discover.interfaces';
import { IAudioState } from '../../interfaces/creator.interfaces';
import { IMediaLoadingState, IVideoEvent } from '../../shared/interfaces/media.interfaces';

import { ESvgTypes } from '../../shared/enums/svg.enums';
import { EPlayButtonUsage } from '../../shared/enums/shared.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';
import { EDiscoverCardState } from '../../enums/discover.enums';
import { EImageUsage } from '../../shared/enums/image-usage.enums';

@Component({
  selector: 'stxt-discover-item',
  standalone: true,
  imports: [
    CommonModule,
    RecaptchaV3Module,
    SvgIconComponent,
    DiscoverCardBadgeComponent,
    PlayVoiceButtonComponent,
    NumberCountPipe,
    VideoComponent,
    ImageComponent
  ],
  templateUrl: './discover-item.component.html',
  styleUrl: './discover-item.component.scss',
  providers: [ReCaptchaV3Service]
})
export class DiscoverItemComponent implements OnInit, OnChanges {
  @Input() discoverItem: IDiscoverItem;
  @Input() userId: string;
  @Input() selectedPreviewAudio: string;
  @Input() isNew: boolean = false;
  @Output() updatePreviewVoiceState: EventEmitter<IAudioState> = new EventEmitter<IAudioState>();
  @ViewChild('previewVoice') previewVoice!: PlayVoiceButtonComponent;
  @ViewChild('primaryVideo') primaryVideo!: VideoComponent;

  public creatorInfo: { creator_name: string; thumbnail_image: string };
  public chatSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'chat-outlined' };
  public chatFilledSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'chat-filled' };
  public clockSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'clock' };
  public clockFilledSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'clock-filled' };
  public verifySvg: ISvgConfig = { fill: ESvgTypes.None, name: 'verified' };
  public isChatLoading: boolean = false;
  public user = JSON.parse(localStorage.getItem('user'));
  public isMobile: boolean = false;
  public primaryImageState: IMediaLoadingState = {
    isLoaded: false,
    isRendered: false,
    isError: false
  };

  protected readonly EPlayButtonUsage = EPlayButtonUsage;
  protected readonly EImageUsage = EImageUsage;

  private isRecaptchaEnabled: boolean;
  private isAdminAccessEnabled: boolean;

  constructor(
    public router: Router,
    public authService: AuthService,
    public modal: NgbModal,
    readonly reCaptchaV3Service: ReCaptchaV3Service,
    readonly navigationIntentService: NavigationIntentService,
    private readonly statsigService: StatsigService,
    private readonly skeletonLoaderService: SkeletonLoaderService,
    private readonly discoverVideoManager: VideoManagerService
  ) {}

  ngOnInit(): void {
    this.creatorInfo = {
      creator_name: this.discoverItem.name,
      thumbnail_image: this.discoverItem.image
    };
    this.checkScreenSize();
    this.checkStatsigFeatureGate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['selectedPreviewAudio'] &&
      this.selectedPreviewAudio !== this.discoverItem.id &&
      this.selectedPreviewAudio !== undefined &&
      this.previewVoice
    ) {
      this.previewVoice.stopAudio();
    }

    if (changes['discoverItem'] && this.discoverItem) {
      this.resetImageState();
    }
  }

  resetImageState(): void {
    this.primaryImageState = {
      isLoaded: false,
      isRendered: false,
      isError: false
    };
  }

  onPrimaryImageLoad(event: Event): void {
    this.primaryImageState.isLoaded = true;
    this.skeletonLoaderService.handleImageStateChange(event, this.primaryImageState).then(() => {
      this.primaryImageState.isRendered = true;
    });
  }

  onPrimaryImageError(): void {
    this.primaryImageState = {
      isLoaded: true,
      isRendered: true,
      isError: true
    };
  }

  isVideo(): boolean {
    return !!(this.discoverItem?.video && this.discoverItem.video.length > 0);
  }

  isImage(): boolean {
    return !!(this.discoverItem?.image && this.discoverItem.image.length > 0);
  }

  onDiscoverCardMouseEnter(): void {
    if (!this.isVideo()) {
      this.stopOtherVideos();
      return;
    }
    this.stopOtherVideos();
    this.playVideo();
  }

  onDiscoverCardMouseLeave(): void {
    if (!this.isVideo()) return;
    this.resetVideo();
  }

  async openChat(event: Event): Promise<void> {
    if (!this.discoverItem.ready && !this.isAdminAccessEnabled) return;
    event.stopPropagation();
    if (this.isChatLoading) return;
    this.isChatLoading = true;

    try {
      await this.processChatNavigation();
    } finally {
      this.isChatLoading = false;
    }
  }

  async checkStatsigFeatureGate(): Promise<void> {
    try {
      await this.statsigService.waitForInitialization();
      this.isRecaptchaEnabled = await this.statsigService.checkFeatureGate(EFeatureGates.RECAPTCHA);
      this.isAdminAccessEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.ADMIN_ACCESS
      );
    } catch (error) {
      this.isRecaptchaEnabled = false;
      this.isAdminAccessEnabled = false;
    }
  }

  private async processChatNavigation(): Promise<void> {
    if (!this.user && this.isRecaptchaEnabled) {
      try {
        await firstValueFrom(this.reCaptchaV3Service.execute('chat_now_action'));
      } catch (error) {
        console.error('reCAPTCHA execution error:', error);
        return;
      }
    }

    await this.authService.checkUserCredentials();
    localStorage.setItem('backUrl', '/');
    localStorage.setItem('creatorInfo', JSON.stringify(this.creatorInfo));

    const targetCreatorId = this.discoverItem.id;
    const targetChatRoute = `/chat/${targetCreatorId}`;

    if (this.authService.userExist$.value && this.authService.userLoaded$.value) {
      await this.router.navigateByUrl(targetChatRoute);
    } else {
      this.navigationIntentService.setIntent(targetChatRoute);
      await this.router.navigateByUrl(targetChatRoute);
    }
  }

  trackVoicePlaying(isPlaying: boolean): void {
    this.updatePreviewVoiceState.emit({ id: this.discoverItem.id, isPlaying: isPlaying });
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 640;
  }

  truncate(text: string, length: number): string {
    return this.isMobile && text.length > length ? text.substring(0, length) + '...' : text;
  }

  public getCardState(): EDiscoverCardState {
    if (!this.discoverItem?.ready && !this.isAdminAccessEnabled) {
      return EDiscoverCardState.ComingSoon;
    }
    if (this.isNew) {
      return EDiscoverCardState.New;
    }
    return null;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
  }

  private playVideo(): void {
    const video = document.getElementById(`discover-${this.discoverItem.id}`) as HTMLVideoElement;
    if (!video?.paused) return;

    video.play();
    this.discoverVideoManager.setCurrentPlayingVideo(`discover-${this.discoverItem.id}`);

    if (this.primaryVideo) {
      this.primaryVideo.isPlaying = true;
    }
  }

  onDiscoverCardTouchStart(): void {
    if (!this.isVideo()) return;

    this.stopOtherVideos();
    this.playVideo();
  }

  private resetVideo(): void {
    const video = document.getElementById(`discover-${this.discoverItem.id}`) as HTMLVideoElement;
    if (!video) return;

    video.pause();
    video.currentTime = 0;

    const currentVideoId = `discover-${this.discoverItem.id}`;
    if (this.discoverVideoManager.getCurrentPlayingVideoId() === currentVideoId) {
      this.discoverVideoManager.stopCurrentVideo();
    }
    if (this.primaryVideo) {
      this.primaryVideo.isPlaying = false;
    }
  }

  onVideoEvent(event: IVideoEvent): void {
    if (event.type === 'play') {
      this.handleVideoPlay();
      return;
    }

    if (event.type === 'pause') {
      this.handleVideoPause();
    }
  }

  private handleVideoPlay(): void {
    // When video starts playing via touch/click, ensure only one video plays
    this.stopOtherVideos();
    this.discoverVideoManager.setCurrentPlayingVideo(`discover-${this.discoverItem.id}`);

    if (!this.primaryVideo) return;

    this.primaryVideo.isPlaying = true;
  }

  private handleVideoPause(): void {
    const currentVideoId = `discover-${this.discoverItem.id}`;

    if (this.discoverVideoManager.getCurrentPlayingVideoId() === currentVideoId) {
      this.discoverVideoManager.stopCurrentVideo();
    }

    if (!this.primaryVideo) {
      return;
    }

    this.primaryVideo.isPlaying = false;
  }

  private stopOtherVideos(): void {
    const currentVideoId = `discover-${this.discoverItem.id}`;
    const playingVideoId = this.discoverVideoManager.getCurrentPlayingVideoId();

    if (playingVideoId && playingVideoId !== currentVideoId) {
      // Stop the currently playing video
      const playingVideo = document.getElementById(playingVideoId) as HTMLVideoElement;
      if (playingVideo && !playingVideo.paused) {
        playingVideo.pause();
        playingVideo.currentTime = 0;
      }
      this.discoverVideoManager.stopCurrentVideo();
    }
  }
}
